import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Cake, X, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const defaultText = (b, sender) => `Bonjour ${b.name},\n\nToute l'équipe vous souhaite un très joyeux anniversaire ! Nous vous souhaitons une excellente journée et une belle année à venir.\n\nBien cordialement,\n${sender}`;

function BirthdayModal({ b, sender, onClose, onSent }) {
  const [text, setText] = useState(defaultText(b, sender));
  const [channels, setChannels] = useState(b.email ? ["email", "message"] : ["message"]);
  const [busy, setBusy] = useState(false);
  const toggle = (c) => setChannels((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);
  const send = async () => {
    if (!channels.length) { toast.error("Choisissez au moins un canal"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/dashboard/birthdays/send", { key: b.key, tenant_id: b.tenant_id, text, channels });
      toast.success(`Message envoyé${data.conversation_id ? " · messagerie" : ""}${data.email_sent ? " · e-mail" : (channels.includes("email") ? " · e-mail non parti" : "")}`);
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[96] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="birthday-modal">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Cake className="w-5 h-5 text-pink-500" /> Souhaiter l'anniversaire de {b.name}</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep" data-testid="birthday-close"><X className="w-5 h-5" /></button>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} className="w-full px-3 py-2.5 rounded-xl border border-brand-soft text-sm outline-none focus:border-brand-lime" data-testid="birthday-text" />
        <div className="flex flex-wrap gap-4 text-sm text-brand-greenDeep">
          <label className={`flex items-center gap-2 ${b.email ? "cursor-pointer" : "opacity-50"}`}><input type="checkbox" disabled={!b.email} checked={channels.includes("email")} onChange={() => toggle("email")} className="accent-brand-greenDeep" data-testid="birthday-ch-email" /> E-mail{b.email ? ` (${b.email})` : " (aucune adresse)"}</label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={channels.includes("message")} onChange={() => toggle("message")} className="accent-brand-greenDeep" data-testid="birthday-ch-message" /> Messagerie de l'application</label>
        </div>
        <button onClick={send} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm disabled:opacity-60" data-testid="birthday-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function BirthdayReminders({ items, sender, onChange }) {
  const [open, setOpen] = useState(null);
  if (!items.length) return null;
  const when = (d) => (d === 0 ? "Aujourd'hui" : d > 0 ? `Dans ${d} j` : `Il y a ${-d} j`);
  return (
    <div className="mb-4 space-y-1" data-testid="birthday-reminders">
      {items.map((b) => (
        <div key={b.key} className="flex items-center gap-3 py-2 px-3 rounded-xl bg-pink-50/70 border border-pink-100" data-testid={`birthday-${b.tenant_id}`}>
          <Cake className="w-5 h-5 text-pink-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-brand-greenDeep truncate">Anniversaire de {b.name}{b.age ? ` (${b.age} ans)` : ""}</p>
            <p className="text-xs text-brand-green/50">{when(b.days)} · {new Date(b.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}{b.property ? ` · ${b.property}` : ""}</p>
          </div>
          <button onClick={() => setOpen(b)} className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full bg-pink-500 hover:bg-pink-600 text-white" data-testid={`birthday-wish-${b.tenant_id}`}>Souhaiter</button>
        </div>
      ))}
      {open && <BirthdayModal b={open} sender={sender} onClose={() => setOpen(null)} onSent={() => { setOpen(null); onChange(); }} />}
    </div>
  );
}
